import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, priceTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateMachineMutation, useUpdateMachineMutation } from '../../../../hooks/mutations/useMachineMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { Machine } from '../../../../services/apiSLTP/materials/machines';
import { MachineType } from '../../../../services/apiSLTP/materials/machinesTypes';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomDateInput from '../../../ui/CustomDateInput';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    machine: Machine | null;
    machines: Machine[];
    users: User[];
    machineTypes: MachineType[];
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateMachineModal: React.FC<ModalProps> = ({
    machine,
    showEditModal,
    setShowEditModal,
    users,
    machineTypes,
    machines,
    isEditMode = true,
    refetch,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [designation, setDesignation] = useState(machine?.designation || '');
    const [codeProC, setCodeProC] = useState(machine?.codeProC || '');
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);
    const [selectedMachineType, setSelectedMachineType] = useState({ label: '', value: '' });
    const [crudStatusEnumState, setCrudStatusState] = useState(machine?.crudStatus || crudStatusEnum.OK);
    const [price, setPrice] = useState(typeof machine?.price === 'number' ? machine?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        machine?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(machine?.priceType))),
                  value: (machine?.priceType).toString(),
              }
            : null
    );
    const [model, setModel] = useState(machine?.model || '');
    const [baseName, setBaseName] = useState(machine?.baseName || '');
    const [startDate, setStartDate] = useState<Date | null>(machine?.startDate ? new Date(machine.startDate) : null);
    const [endDate, setEndDate] = useState<Date | null>(machine?.endDate ? new Date(machine.endDate) : null);

    const { user } = useAuth();

    const {
        mutate: mutateMachine,
        isSuccess: isSuccessMachine,
        isError: isErrorMachine,
        isLoading: isLoadingMachine,
        error: errorMachine,
        data: dataMachine,
    } = useUpdateMachineMutation({ onSuccess: () => refetch(), onError: (error) => console.error('Error updating machine:', error.message) });

    const {
        mutate: addMachine,
        isSuccess: isSuccessAdded,
        isError: isErrorAdded,
        isLoading: isLoadingAdded,
        error: errorAdded,
        data: dataAdded,
    } = useCreateMachineMutation({ onSuccess: () => refetch(), onError: (error) => console.error('Error creating machine:', error.message) });

    const handleActiveChange = (isActive: boolean) => {
        setCrudStatusState(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    useEffect(() => {
        const user = users.find((user) => user._id === machine?.userId?._id);
        if (user) setSelectedUser({ label: formatName(user.firstName, user.lastName), value: user._id });

        const machineType = machineTypes.find((type) => type._id === machine?.machineTypeId?._id);
        if (machineType) setSelectedMachineType({ label: machineType.designation, value: machineType._id });
    }, [machine, users, machineTypes]);

    return (
        <ModalBody
            title={t('machine')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!codeProC || !designation || !selectedMachineType.value || !baseName}
            onSubmit={() => {
                const machineData = {
                    codeProC,
                    designation,
                    machineTypeId: selectedMachineType.value,
                    userId: selectedUser ? selectedUser.value : null,
                    crudStatus: crudStatusEnumState,
                    priceType: priceType ? parseInt(priceType.value) : null,
                    price: parseFloat(price),
                    model,
                    baseName,
                    startDate: startDate ? startDate.toISOString() : null,
                    endDate: endDate ? endDate.toISOString() : null,
                };
                isEditMode ? mutateMachine({ machineId: machine?._id || '', data: machineData }) : addMachine(machineData);
            }}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} errorText={t('error')} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={setCodeProC} errorText={t('error')} required />
            <ToggleLine
                checked={crudStatusEnumState === crudStatusEnum.OK}
                onChange={handleActiveChange}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput value={baseName} onChange={setBaseName} label={t('baseName')} required />
            <DropDown
                items={machineTypes.map((type) => ({ label: type.designation, value: type._id }))}
                selectedItem={selectedMachineType}
                handleChange={(event, value) => setSelectedMachineType({ label: value?.label || '', value: value?.value || '' })}
                inputLabel={t('type')}
                required
            />
            <DropDown
                items={users.map((user) => ({ label: formatName(user.firstName, user.lastName), value: user._id }))}
                selectedItem={selectedUser}
                handleChange={(event, value) => setSelectedUser({ label: value?.label || '', value: value?.value || '' })}
                inputLabel={t('user')}
                disableClearable={false}
            />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(priceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    setPriceType(value);
                }}
                inputLabel={t('pricing')}
                disableClearable={false}
            />
            <CustomDateInput
                label={t('startDate')}
                onChange={(value) => (value ? setStartDate(new Date(value?.toISOString())) : null)}
                defaultDate={startDate}
            />
            <CustomDateInput
                label={t('endDate')}
                onChange={(value) => (value ? setEndDate(new Date(value?.toISOString())) : null)}
                defaultDate={endDate}
            />
            <CustomTextInput value={model} onChange={setModel} label={t('model')} />
        </ModalBody>
    );
};

export default UpdateMachineModal;
